// Searching for a game in the Match module (Ben, 2026-10-30: "a way of searching for games,
// rather than being locked on the initial game"). One box that reads football:
//   "Arsenal"          → Arsenal's games, newest first
//   "Arsenal Chelsea"  → every meeting of the two we have (a head-to-head, across seasons)
//   "Arsenal 5-2"      → by score (the first team's goals first); "5 2" or "5:2" too
//   "5-2", "5"         → numbers alone (Ben, 2026-10-30): that score, or a side scoring 5, in the
//                        leagues of what's on the canvas
//   "Arsenal October"  → by month
//   "Arsenal Everton 2025" → a year: the seasons it falls in (24/25 and 25/26; Ben, 2026-10-31)
//   "Arsenal 25/26", "2025/26", "2025-26" → that season; "October 2025" → that month
// Teams are found as the Bio's search finds them (every word starts a word of the club's name).
// League games only: a team's games are its home league's; two teams' are looked for in BOTH their
// home leagues (Ben, 2026-10-30: Swansea, now in the Championship, met Liverpool in the Premier
// League). Never "there were none": a search that finds nothing says what it searched.

import type { MatchSummary, MatchesResponse, SeasonsResponse } from "@catbento/shared";
import type { LiveApi } from "./api.js";
import { foldText } from "../data/fuzzy-search.js";
import { matchOfResult, type MatchRef } from "./match.js";

/** A team the box found: its id, name and home league. */
export interface FoundTeam {
  id: number;
  name: string;
  leagueId: number;
}

export interface GameQuery {
  /** What's left once the score and month are taken out: the team name(s). */
  teamText: string;
  /** "5-2", "5–2", "5:2", "5 2": the first team's goals first. */
  score: [number, number] | null;
  /** One number on its own: a side (the first team, when there is one) scored this many. */
  goals: number | null;
  /** 0 = January. */
  month: number | null;
  /** A year on its own ("2025"): the seasons it falls in (24/25 and 25/26; a calendar-year league's
   *  2025). With a month, that month of that year. */
  year: number | null;
  /** A season typed out ("25/26", "2025/26", "2025-26"): the year it starts. */
  season: number | null;
}

const JOINING_WORDS: ReadonlySet<string> = new Set(["v", "vs", "versus", "against", "and", "in", "season"]);

/** "25" → 2025, "98" → 1998, "2025" → 2025. */
function fullYear(digits: string): number {
  const n = Number(digits);
  return digits.length === 4 ? n : n >= 50 ? 1900 + n : 2000 + n;
}

const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];

/** Read the box. Pure. */
export function parseGameQuery(raw: string): GameQuery {
  let rest = raw;
  // A season first, before a score can take it: "25/26", "2025/26", "2025/2026" (a slash: always),
  // "2025-26" (a dash: only after four digits, so "1-2" stays a score). The years must follow on.
  let season: number | null = null;
  const sm = rest.match(/(?:^|\s)((?:19|20)\d{2}|\d{2})\s*([/\-–])\s*((?:19|20)\d{2}|\d{2})(?=\s|$)/);
  if (sm && (sm[2] === "/" || sm[1]!.length === 4)) {
    const start = fullYear(sm[1]!);
    if (fullYear(sm[3]!) % 100 === (start + 1) % 100) {
      season = start;
      rest = rest.replace(sm[0], " ");
    }
  }
  // A year on its own ("2025").
  let year: number | null = null;
  const ym = rest.match(/(?:^|\s)((?:19|20)\d{2})(?=\s|$)/);
  if (ym) {
    year = Number(ym[1]);
    rest = rest.replace(ym[0], " ");
  }
  // Then the score: folding turns its dash into a space.
  let score: [number, number] | null = null;
  const s = rest.match(/(?:^|\s)(\d{1,2})\s*[-–:]\s*(\d{1,2})(?=\s|$)/);
  if (s) {
    score = [Number(s[1]), Number(s[2])];
    rest = rest.replace(s[0], " ");
  }
  let text = ` ${foldText(rest)} `;
  // Numbers without a dash: two make a score ("5 2"), one a side's goals ("Arsenal 4").
  let goals: number | null = null;
  if (!score) {
    const nums = text.trim().split(/\s+/).filter((w) => /^\d{1,2}$/.test(w));
    if (nums.length >= 2) score = [Number(nums[0]), Number(nums[1])];
    else if (nums.length === 1) goals = Number(nums[0]);
    for (const n of nums.slice(0, 2)) text = text.replace(` ${n} `, " ");
  }
  let month: number | null = null;
  for (const word of text.trim().split(/\s+/)) {
    // "oct", "octo", "october" — three letters at least, so "mar" works but "ma" doesn't.
    const m = word.length >= 3 ? MONTHS.findIndex((name) => name.startsWith(word)) : -1;
    if (m >= 0) {
      month = m;
      text = text.replace(` ${word} `, " ");
      break;
    }
  }
  // The words between two teams ("Arsenal v Chelsea") aren't part of either name.
  const teamText = text
    .trim()
    .split(/\s+/)
    .filter((w) => w && !JOINING_WORDS.has(w))
    .join(" ");
  return { teamText, score, goals, month, year, season };
}

/** The year a season starts and whether it's a calendar-year league's (NWSL's "2025"), from its id
 *  ("52025" is 25/26, as humanizeSeasonCode reads it) or its name ("25/26", "2025/2026",
 *  "2025-2026", "2025");
 *  null if neither says. Pure. */
export function seasonStart(code: string, name: string): { start: number; calendar: boolean } | null {
  if (/^5\d{4}$/.test(code)) return { start: Number(code.slice(1)), calendar: false };
  const span = name.match(/((?:19|20)\d{2}|\d{2})\s*[/\-–]\s*((?:19|20)\d{2}|\d{2})/);
  if (span) return { start: fullYear(span[1]!), calendar: false };
  const cal = (/^(19|20)\d{2}$/.test(code) ? code : name.trim()).match(/^(?:19|20)\d{2}$/);
  return cal ? { start: Number(cal[0]), calendar: true } : null;
}

/** Does a season fit the year or season typed? Any does when none was; one that says nothing of
 *  its years is kept (never hidden for want of a name). Pure. */
export function seasonFits(code: string, name: string, q: GameQuery): boolean {
  if (q.season === null && q.year === null) return true;
  const s = seasonStart(code, name);
  if (!s) return true;
  if (q.season !== null) return s.calendar ? s.start === q.season || s.start === q.season + 1 : s.start === q.season;
  const y = q.year as number;
  // A year falls in the season ending in it and the one starting in it (2025: 24/25 and 25/26).
  return s.calendar ? s.start === y : s.start === y || s.start === y - 1;
}

/** How many of each half's teams are tried for a pair from one league. */
const PAIR_CANDIDATES = 5;

/** The team(s) in the text: the whole of it as one team, else two teams split at a word ("man city
 *  arsenal" → Man City + Arsenal). `find` = the teams for a text, best first. Of the splits, the
 *  first whose two teams share a league wins: only they have league games together, and a split in
 *  the wrong place finds odd teams ("man u arse": "man" + "u arse" = Man City + Arsenal U18, Ben
 *  2026-11-01, where "man u" + "arse" is Man United + Arsenal). With no such pair, the first split
 *  that finds two teams. Pure. */
export function findTeams(teamText: string, find: (text: string) => readonly FoundTeam[]): FoundTeam[] {
  if (!teamText) return [];
  const whole = find(teamText)[0];
  if (whole) return [whole];
  const words = teamText.split(" ");
  let fallback: FoundTeam[] = [];
  for (let i = 1; i < words.length; i++) {
    const as = find(words.slice(0, i).join(" ")).slice(0, PAIR_CANDIDATES);
    const bs = find(words.slice(i).join(" ")).slice(0, PAIR_CANDIDATES);
    for (const a of as) {
      const b = bs.find((t) => t.leagueId === a.leagueId && t.id !== a.id);
      if (b) return [a, b];
    }
    if (!fallback.length && as[0] && bs[0] && as[0].id !== bs[0].id) fallback = [as[0], bs[0]];
  }
  return fallback;
}

/** Does a game's score fit the typed numbers in the order typed: the first team's goals first
 *  ("chelsea 5": Chelsea scored 5)? Without a team, home first. */
function inTypedOrder(m: Pick<MatchSummary, "homeTeamId" | "homeScore" | "awayScore">, a: FoundTeam | undefined, q: GameQuery): boolean {
  const ours = !a || m.homeTeamId === a.id ? [m.homeScore, m.awayScore] : [m.awayScore, m.homeScore];
  if (q.score) return ours[0] === q.score[0] && ours[1] === q.score[1];
  if (q.goals !== null) return ours[0] === q.goals;
  return true;
}

/** The games that fit: played, the team's (both teams' for two), the score, the month. The order
 *  things are typed in is a guide, never a filter (Ben, 2026-10-31: "chel arse 5" must find Arsenal
 *  5–0 Chelsea): a score or a side's goals fits either side, and games fitting the typed order come
 *  first; then newest first. Pure. */
export function filterGames<M extends MatchSummary>(matches: readonly M[], teams: readonly FoundTeam[], q: GameQuery): M[] {
  const [a, b] = teams;
  return matches
    .filter((m) => {
      if (m.homeScore === null || m.awayScore === null) return false;
      const sides = [m.homeTeamId, m.awayTeamId];
      if (a && !sides.includes(a.id)) return false;
      if (b && !sides.includes(b.id)) return false;
      if (q.score) {
        const [x, y] = q.score;
        if (!((m.homeScore === x && m.awayScore === y) || (m.homeScore === y && m.awayScore === x))) return false;
      }
      if (q.goals !== null && m.homeScore !== q.goals && m.awayScore !== q.goals) return false;
      if (q.month !== null && Number(m.matchDate.slice(5, 7)) - 1 !== q.month) return false;
      // "October 2025": that month of that year.
      if (q.month !== null && q.year !== null && Number(m.matchDate.slice(0, 4)) !== q.year) return false;
      return true;
    })
    .sort((x, y) => {
      const fit = Number(inTypedOrder(y, a, q)) - Number(inTypedOrder(x, a, q));
      if (fit) return fit;
      return x.matchDate < y.matchDate ? 1 : x.matchDate > y.matchDate ? -1 : y.matchId - x.matchId;
    });
}

/** Games from several seasons in one list: the typed order first, then newest first (as
 *  filterGames ranks one season's). Pure. */
export function rankGames(games: MatchRef[], a: FoundTeam | undefined, q: GameQuery): MatchRef[] {
  const typed = (g: MatchRef) =>
    inTypedOrder({ homeTeamId: g.home.id, homeScore: g.homeScore, awayScore: g.awayScore }, a, q);
  return games.sort((x, y) => {
    const fit = Number(typed(y)) - Number(typed(x));
    if (fit) return fit;
    return x.date < y.date ? 1 : x.date > y.date ? -1 : y.id - x.id;
  });
}

// --- loading -----------------------------------------------------------------------------------

const seasonLists = new Map<number, Promise<{ code: string; name: string }[]>>();
const seasonGames = new Map<string, Promise<MatchSummary[]>>();

/** A league's seasons, newest first (asked once per page). */
function seasonsOf(api: LiveApi, competitionId: number): Promise<{ code: string; name: string }[]> {
  let p = seasonLists.get(competitionId);
  if (!p) {
    p = api.get<SeasonsResponse>(`/seasons?competitionId=${competitionId}`).then((r) => r.seasons.map((s) => ({ code: String(s.id), name: s.name })));
    seasonLists.set(competitionId, p);
    // A failure isn't kept: the next search asks again.
    p.catch(() => seasonLists.delete(competitionId));
  }
  return p;
}

/** One league season's games (asked once per page). */
function gamesOf(api: LiveApi, competitionId: number, season: string): Promise<MatchSummary[]> {
  const key = `${competitionId}:${season}`;
  let p = seasonGames.get(key);
  if (!p) {
    p = api.get<MatchesResponse>(`/matches?competitionId=${competitionId}&seasonId=${season}`).then((r) => r.matches);
    seasonGames.set(key, p);
    p.catch(() => seasonGames.delete(key));
  }
  return p;
}

/** The games in some leagues that fit a query with no team (numbers alone): each league's newest
 *  `seasons`, newest first. */
export async function searchLeagueGames(api: LiveApi, leagueIds: readonly number[], q: GameQuery, seasons: number): Promise<MatchRef[]> {
  const out: MatchRef[] = [];
  await Promise.all(
    leagueIds.map(async (leagueId) => {
      const codes = (await seasonsOf(api, leagueId))
        .filter((s) => seasonFits(s.code, s.name, q))
        .slice(0, seasons)
        .map((s) => s.code);
      for (const code of codes) {
        for (const m of filterGames(await gamesOf(api, leagueId, code), [], q)) {
          const ref = matchOfResult(m, leagueId, code);
          if (ref) out.push(ref);
        }
      }
    }),
  );
  return rankGames(out, undefined, q);
}

/** What a search looked through: each league, and its seasons (newest first). */
export type SearchedLeagues = { leagueId: number; seasons: string[] }[];

/** The games for a query, and what was searched: each team's home league (two teams: both, each
 *  once), every season we have, newest first. `seasons` caps how many per league (newest first) —
 *  a plain team needs only the latest. */
export async function searchGamesIn(
  api: LiveApi,
  teams: readonly FoundTeam[],
  q: GameQuery,
  seasons = Infinity,
): Promise<{ games: MatchRef[]; searched: SearchedLeagues }> {
  const leagueIds = [...new Set(teams.map((t) => t.leagueId))];
  const searched: SearchedLeagues = [];
  const out: MatchRef[] = [];
  const seen = new Set<number>();
  await Promise.all(
    leagueIds.map(async (leagueId) => {
      const codes = (await seasonsOf(api, leagueId))
        .filter((s) => seasonFits(s.code, s.name, q))
        .slice(0, seasons)
        .map((s) => s.code);
      searched.push({ leagueId, seasons: codes });
      const perSeason = await Promise.all(codes.map(async (code) => ({ code, games: await gamesOf(api, leagueId, code) })));
      for (const { code, games } of perSeason) {
        for (const m of filterGames(games, teams, q)) {
          if (seen.has(m.matchId)) continue;
          seen.add(m.matchId);
          const ref = matchOfResult(m, leagueId, code);
          if (ref) out.push(ref);
        }
      }
    }),
  );
  searched.sort((a, b) => leagueIds.indexOf(a.leagueId) - leagueIds.indexOf(b.leagueId));
  return { games: rankGames(out, teams[0], q), searched };
}

/** The games for a query (searchGamesIn without what was searched). */
export async function searchGames(api: LiveApi, teams: readonly FoundTeam[], q: GameQuery, seasons = Infinity): Promise<MatchRef[]> {
  return (await searchGamesIn(api, teams, q, seasons)).games;
}
